import React, { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

interface AlertDialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AlertDialogContext = createContext<AlertDialogContextValue>({ open: false, onOpenChange: () => {} });

interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({ open = false, onOpenChange = () => {}, children }) => (
  <AlertDialogContext.Provider value={{ open, onOpenChange }}>
    {children}
  </AlertDialogContext.Provider>
);

export const AlertDialogContent: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  const { open, onOpenChange } = useContext(AlertDialogContext);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={() => onOpenChange(false)} />
      <div role="dialog" aria-modal="true" className={`relative z-50 w-full max-w-md bg-white rounded-lg shadow-lg border border-border ${className}`}>
        {children}
      </div>
    </div>,
    document.body
  );
};

export const AlertDialogHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`flex flex-col gap-1 p-6 pb-4 ${className}`}>{children}</div>
);

export const AlertDialogFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`flex justify-end gap-3 px-6 pb-6 ${className}`}>{children}</div>
);

export const AlertDialogTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <h2 className={`text-lg font-semibold text-text-primary ${className}`}>{children}</h2>
);

export const AlertDialogDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`text-sm text-text-secondary ${className}`}>{children}</p>
);

interface AlertDialogActionProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

export const AlertDialogAction: React.FC<AlertDialogActionProps> = ({ children, className = '', ...props }) => (
  <button
    className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const AlertDialogCancel: React.FC<AlertDialogActionProps> = ({ children, className = '', ...props }) => {
  const { onOpenChange } = useContext(AlertDialogContext);
  return (
    <button
      className={`inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-border text-text-primary hover:bg-gray-50 transition-colors focus:outline-none ${className}`}
      onClick={() => onOpenChange(false)}
      {...props}
    >
      {children}
    </button>
  );
};
